import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const TripInfoTab = ({ tripData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    tripName: tripData?.tripName || '',
    crewSize: tripData?.crewSize || 2,
    duration: tripData?.duration || 3
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData?.tripName?.trim()) {
      newErrors.tripName = 'Trip name is required';
    } else if (formData?.tripName?.trim()?.length < 3) {
      newErrors.tripName = 'Trip name must be at least 3 characters';
    }

    const crewSize = parseInt(formData?.crewSize);
    if (!crewSize || crewSize < 1) {
      newErrors.crewSize = 'Crew size must be at least 1';
    } else if (!tripData?.isPremium && crewSize > 3) {
      newErrors.crewSize = 'Trial mode limited to 3 crew members';
    }

    const duration = parseInt(formData?.duration);
    if (!duration || duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    } else if (!tripData?.isPremium && duration > 7) {
      newErrors.duration = 'Trial mode limited to 7 days';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    const result = await onUpdate({
      tripName: formData?.tripName,
      crewSize: parseInt(formData?.crewSize),
      duration: parseInt(formData?.duration),
      completedDays: [] // Reset completed days when trip info is updated
    });

    setSaving(false);
    if (result?.success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      tripName: tripData?.tripName || '',
      crewSize: tripData?.crewSize || 2,
      duration: tripData?.duration || 3
    });
    setErrors({});
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Trip Information
        </h2>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            size="sm"
          >
            <Icon name="Edit" size={16} />
            <span className="ml-2">Edit</span>
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Trip Name
          </label>
          <Input
            name="tripName"
            value={formData?.tripName}
            onChange={handleChange}
            disabled={!isEditing}
            placeholder="Enter trip name"
            error={errors?.tripName}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Crew Size
          </label>
          <Input
            type="number"
            name="crewSize"
            value={formData?.crewSize}
            onChange={handleChange}
            disabled={!isEditing}
            min="1"
            max={tripData?.isPremium ? "20" : "3"}
            error={errors?.crewSize}
          />
          {!tripData?.isPremium && (
            <p className="text-xs text-muted-foreground mt-1">
              Trial mode: Maximum 3 crew members
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Trip Duration (days)
          </label>
          <Input
            type="number"
            name="duration"
            value={formData?.duration}
            onChange={handleChange}
            disabled={!isEditing}
            min="1"
            max={tripData?.isPremium ? "60" : "7"}
            error={errors?.duration}
          />
          {!tripData?.isPremium && (
            <p className="text-xs text-muted-foreground mt-1">
              Trial mode: Maximum 7 days
            </p>
          )}
        </div>

        <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Trip Status
            </span>
            <span className="text-sm px-3 py-1 rounded-full bg-green-500/10 text-green-500">
              {tripData?.status || 'Active'}
            </span>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button onClick={handleCancel} variant="outline" disabled={saving}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default TripInfoTab;